import React from 'react';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { CmsModel } from 'client/data/models/cms';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import {
  TypeRankingsModel,
  buildTypeRankingsPath,
  buildTypeRankingsPathForAboveTheFold,
  buildTypeRankingsPathForBelowTheFold,
} from 'client/data/models/type-rankings';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { getReviewLinksPath, getRankingsContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { getMostSoldTypeContentPath } from 'site-modules/shared/utils/upper-funnel/most-sold-type-infographic';
import { hasSubsegments } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { EditorialModel, buildTypeArticlesPath } from 'client/data/models/editorial';
import { TypePageDecorator } from 'site-modules/upper-funnel/decorators/type-page-decorator';
import { TypePageDecoratorPlat3761 } from 'site-modules/upper-funnel/decorators/type-page-decorator-plat3761';
import { defineSeoContent } from 'site-modules/upper-funnel/utils/seo-content';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { TYPES_WITH_MOST_SOLD_GRAPH } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/constants';
import { HERO_BG_PATHS } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';
import { CAR_TYPES, PAGE_CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { PageModel } from 'client/data/models/page';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { TYPES_FOR_PRELOAD_ABOVE_FOLD } from 'client/site-modules/upper-funnel/constants/types-for-preload-above-fold';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { forceDisableLooseEtag } from 'client/actions/etag';

import { TypePageV2 } from './type';

const shouldCarTypeUsePreloadAboveFold = type => TYPES_FOR_PRELOAD_ABOVE_FOLD.has(type);

export const pageDefinitionConfig = ({ type, featureFlags }) => ({
  name: 'new_type_index',
  category: 'new_type_index',
  preload: store => {
    const preloader = new ModelPreloader(store);

    const state = store.getState();
    const { mobile } = state;

    const rankingsPath = buildTypeRankingsPath(type);
    preloader.resolve(`content["${getReviewLinksPath(type)}"]`, CmsModel);
    preloader.resolve(`content["${getRankingsContentPath(type)}"]`, CmsModel);
    preloader.resolve(`content["${getMostSoldTypeContentPath(type)}"]`, CmsModel);
    preloader.resolve(buildTypeArticlesPath(type), EditorialModel);

    if (type !== PAGE_CAR_TYPES.MINIVAN) {
      preloader.resolve('vehicleShowcaseContent', HomeModulesModel);
    }

    if (type === CAR_TYPES.ELECTRIC) {
      preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
    }

    // this path resolves rankings path data first, then uses that to resolve above the fold data
    preloader.resolve(buildTypeRankingsPathForAboveTheFold(type), TypeRankingsModel);

    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

    const vehicle = { type: { niceName: type } };
    store.dispatch(updateVehicle(vehicle));

    const disableLooseEtag =
      ExperimentUtil.getForcedOrAssignedRecipeName({
        state: store.getState(),
        campaignName: 'ads-11082',
        defaultVal: 'ctrl',
      }) === 'ctrl';
    store.dispatch(forceDisableLooseEtag(disableLooseEtag));

    return preloader.load().then(() => {
      // resolves below the fold data by getting the rankings path data
      // rankings path data was already resolved in the previous preloader.load()
      preloader.resolve(buildTypeRankingsPathForBelowTheFold(type), TypeRankingsModel);
      setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

      return preloader.load().then(modelState => {
        const vehicleEditorialRankings = modelState.get(rankingsPath, TypeRankingsModel);
        const siteServedAds = modelState.get(
          getCreativeConfigAdPathByVehicle(mobile, vehicle),
          NativeAdsCreativeConfigModel
        );

        const imageHref = mobile ? HERO_BG_PATHS.sm : HERO_BG_PATHS.lg;
        const assetsToPreload = [{ href: imageHref, as: 'image' }];
        preloader.set('server.preloadedAssets', PageModel, assetsToPreload);

        store.dispatch(
          setCriticalCss({
            type,
            criticalCssHeight: mobile ? 1750 : 1300,
            hasAnchorNav: hasSubsegments(vehicleEditorialRankings) || TYPES_WITH_MOST_SOLD_GRAPH.includes(type),
            ...siteServedAdsCriticalCss(siteServedAds),
            topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
          })
        );
        return preloader.load();
      });
    });
  },
  // it only works for coupe type
  preloadAboveFold: shouldCarTypeUsePreloadAboveFold(type)
    ? store => {
        const preloader = new ModelPreloader(store);

        const state = store.getState();
        const { mobile } = state;

        const rankingsPath = buildTypeRankingsPath(type);
        preloader.resolve(buildTypeRankingsPathForAboveTheFold(type), TypeRankingsModel);

        const vehicle = { type: { niceName: type } };
        store.dispatch(updateVehicle(vehicle));
        setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

        return preloader.load().then(modelState => {
          const vehicleEditorialRankings = modelState.get(rankingsPath, TypeRankingsModel);
          const siteServedAds = modelState.get(
            getCreativeConfigAdPathByVehicle(mobile, vehicle),
            NativeAdsCreativeConfigModel
          );

          const imageHref = mobile ? HERO_BG_PATHS.sm : HERO_BG_PATHS.lg;
          const assetsToPreload = [{ href: imageHref, as: 'image' }];
          preloader.set('server.preloadedAssets', PageModel, assetsToPreload);

          store.dispatch(
            setCriticalCss({
              type,
              criticalCssHeight: mobile ? 1750 : 1300,
              hasAnchorNav: hasSubsegments(vehicleEditorialRankings) || TYPES_WITH_MOST_SOLD_GRAPH.includes(type),
              ...siteServedAdsCriticalCss(siteServedAds),
              topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
            })
          );
          return preloader.load();
        });
      }
    : null,
  component: props => <TypePageV2 type={type} {...props} />,
  decorator:
    featureFlags?.noAdsOnCoupe && shouldCarTypeUsePreloadAboveFold(type)
      ? TypePageDecoratorPlat3761
      : TypePageDecorator,
  seo: (state, props) => defineSeoContent(state, props, type),
  options: {
    hasVehicleContext: true,
    isEditorialPageWithInlineMobileAd: true,
  },
});

export default params => pageDefinition(pageDefinitionConfig(params));
